import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
	ActionButton,
	EmptyState,
	FilterChip,
	ListPageChartCard,
	ListPageCharts,
	ListPageLayout,
	Pagination,
	SectionCard,
	useToast,
} from '@practium/ui';
import type { SectionTab } from '@practium/ui';
import { getApiErrorMessage, useAddLandingPlan, useGetPlansStats } from '@practium/features/api';
import { PiePercentageChart, ComparisonLineChart } from '@practium/features/dashboard';
import { usePermissions } from '@practium/features/permissions';
import { AddLandingPlanModal } from '../components/Modals/AddLandingPlanModal';
import { LandingPageSkeleton } from '../components/Skeletons/LandingPageSkeleton';
import { LandingPlansTable } from '../components/Tables/LandingPlansTable/LandingPlansTable';
import { TestimonialsTable } from '../components/Tables/TestimonialsTable/TestimonialsTable';
import { useLandingPage } from '../hooks/useLandingPage';
import type { LandingTab } from '../types/landing.types';
import {
	LANDING_PIE_TITLE,
	LANDING_PIE_SUBTITLE,
	LANDING_SECTION_TITLE,
	LANDING_SECTION_SUBTITLE,
} from '../constants/page.constants';
import { LANDING_ROUTES } from '../constants/landing.constants';
import { TESTIMONIALS_LINE_DATA } from '../constants/charts.constants';

export const LandingControlPage = () => {
	const navigate = useNavigate();
	const { abilities } = usePermissions();
	const canCreateTestimonial = abilities.can('create', 'testimonials');
	const canCreateLandingPlan = abilities.can('create', 'landingPlans');
	const [isAddPlanOpen, setIsAddPlanOpen] = useState(false);
	const { mutateAsync: addLandingPlan, isPending: isAddingPlan } = useAddLandingPlan();
	const toast = useToast();

	const {
		activeTab,
		setActiveTab,
		availablePlans,
		planDistribution,
		hasReachedPlanLimit,
		filteredPlans,
		filteredTestimonials,
		searchQuery,
		setSearchQuery,
		sortAsc,
		toggleSort,
		filterValues,
		setFilterValues,
		filterGroups,
		page,
		setPage,
		totalPages,
		isLoading,
		isPlanError,
		planError,
		refetchPlans,
	} = useLandingPage();
	const {
		data: planStats,
		isLoading: isStatsLoading,
		isFetching: isStatsFetching,
		isError: isStatsError,
		error: statsError,
		refetch: refetchStats,
	} = useGetPlansStats(activeTab === 'plans');

	if (isLoading) return <LandingPageSkeleton />;
	if (isPlanError) {
		return (
			<div role="alert">
				<EmptyState
					title="Não foi possível carregar os planos da landing page"
					description={getApiErrorMessage(planError, 'Tente novamente para carregar os planos.')}
				/>
				<ActionButton text="Tentar novamente" onClick={() => void refetchPlans()} />
			</div>
		);
	}

	const handleAddPlan = async (planId: string) => {
		try {
			await addLandingPlan(planId);
			toast.success('Plano adicionado à landing page');
			setIsAddPlanOpen(false);
		} catch (error) {
			toast.error(getApiErrorMessage(error, 'Não foi possível adicionar o plano à landing page'));
		}
	};

	const lineData = activeTab === 'plans' ? (planStats?.line.data ?? []) : TESTIMONIALS_LINE_DATA;
	const lineTitle = activeTab === 'plans' ? 'Cliques nos Planos' : 'Likes em Depoimentos';
	const lineMainValue = activeTab === 'plans' ? planStats?.line.subtitle : '2 Likes Hoje';
	const lineFooter =
		activeTab === 'plans'
			? planStats && `Cliques de domingo a sábado • ${planStats.line.period}`
			: 'Likes de 1–6 Dez, 2025';
	const growthPercentage = planStats?.line.growthPercentage;
	const lineTrendValue =
		activeTab === 'plans'
			? growthPercentage !== undefined
				? `${Math.abs(growthPercentage).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`
				: undefined
			: '12.3%';
	const lineTrendDirection =
		activeTab === 'plans'
			? growthPercentage === undefined
				? undefined
				: growthPercentage < 0
					? 'down'
					: 'up'
			: 'down';

	const tabs: SectionTab<LandingTab>[] = [
		{
			key: 'plans',
			label: 'Planos',
			content: (
				<LandingPlansTable
					plans={filteredPlans}
					onRowClick={(planId, landingPlanId) =>
						navigate(LANDING_ROUTES.VIEW_PLAN(planId, landingPlanId))
					}
				/>
			),
		},
		{
			key: 'testimonials',
			label: 'Depoimentos',
			content: (
				<TestimonialsTable
					testimonials={filteredTestimonials}
					onRowClick={(id) => navigate(LANDING_ROUTES.VIEW_TESTIMONIAL(id))}
				/>
			),
		},
	];

	return (
		<ListPageLayout
			sidebarWidth="28rem"
			sidebarW1500="22rem"
			sidebarW1300="19rem"
			sidebarW1200="17rem"
			sidebar={
				<ListPageCharts single={activeTab !== 'plans'}>
					{activeTab === 'plans' && (
						<ListPageChartCard>
							<PiePercentageChart
								title={LANDING_PIE_TITLE}
								subtitle={LANDING_PIE_SUBTITLE}
								buttonText="Ver Relatório"
								onButtonClick={() => {}}
								data={planDistribution}
							/>
						</ListPageChartCard>
					)}
					<ListPageChartCard
						maxHeight="30rem"
						isFetching={
							activeTab === 'plans' && isStatsFetching && !isStatsLoading && !isStatsError
						}
					>
						{activeTab === 'plans' && isStatsError ? (
							<div role="alert">
								<EmptyState
									title="Não foi possível carregar os cliques nos planos"
									description={getApiErrorMessage(
										statsError,
										'Tente novamente para carregar as estatísticas.'
									)}
								/>
								<ActionButton text="Tentar novamente" onClick={() => void refetchStats()} />
							</div>
						) : (
							<ComparisonLineChart
								isLoading={activeTab === 'plans' && isStatsLoading}
								title={lineTitle}
								mainValue={lineMainValue}
								trendValue={lineTrendValue}
								trendLabel="vs semana anterior"
								trendDirection={lineTrendDirection}
								footerText={lineFooter}
								buttonText="Ver Relatório"
								onButtonClick={() => {}}
								data={lineData}
								xAxisKey={activeTab === 'plans' ? 'label' : 'day'}
								line1Key={activeTab === 'plans' ? 'current' : 'atual'}
								line1Label="Semana atual"
								line2Key={activeTab === 'plans' ? 'previous' : 'anterior'}
								line2Label="Semana anterior"
								tooltipTitle={activeTab === 'plans' ? 'Cliques' : 'Likes'}
							/>
						)}
					</ListPageChartCard>
				</ListPageCharts>
			}
		>
			<SectionCard
				title={LANDING_SECTION_TITLE}
				subtitle={LANDING_SECTION_SUBTITLE}
				tabs={tabs}
				activeTab={activeTab}
				onTabChange={setActiveTab}
				search={true}
				searchQuery={searchQuery}
				onSearch={setSearchQuery}
				sortAsc={sortAsc}
				onToggleSort={toggleSort}
				filterSlot={
					filterGroups.length > 0 ? (
						<FilterChip
							values={filterValues}
							onChange={setFilterValues}
							groups={filterGroups}
							placeholder="Filtros"
						/>
					) : undefined
				}
				footer={<Pagination page={page} totalPages={totalPages} onPageChange={setPage} />}
				headerAction={
					<>
						{activeTab === 'testimonials' && canCreateTestimonial && (
							<ActionButton
								text="Adicionar"
								onClick={() => navigate(LANDING_ROUTES.NEW_TESTIMONIAL)}
							/>
						)}
						{activeTab === 'plans' && canCreateLandingPlan && (
							<ActionButton text="Adicionar plano" onClick={() => setIsAddPlanOpen(true)} />
						)}
					</>
				}
			/>
			{isAddPlanOpen && (
				<AddLandingPlanModal
					plans={availablePlans}
					isLoading={isLoading}
					isAdding={isAddingPlan}
					hasReachedLimit={hasReachedPlanLimit}
					onAdd={handleAddPlan}
					onClose={() => setIsAddPlanOpen(false)}
				/>
			)}
		</ListPageLayout>
	);
};
